import React, { useState, useMemo } from "react";
import { Search, Settings, X, ChevronLeft, ChevronRight } from "lucide-react";

const FILTER_OPS = [
  { value: "contains",    label: "contains"    },
  { value: "equals",      label: "equals"      },
  { value: "starts_with", label: "starts with" },
  { value: "not_equals",  label: "≠"           },
  { value: "gt",          label: ">"           },
  { value: "lt",          label: "<"           },
];

const IS_AMOUNT = /amount|amt|value|credit|debit|balance|total|sum/i;

function applyFilter(row, textSearch, field, op, value) {
  if (textSearch.trim()) {
    const hay = Object.values(row || {}).join(" ").toLowerCase();
    if (!hay.includes(textSearch.toLowerCase())) return false;
  }
  if (field && value.trim()) {
    const cell = String(row?.[field] ?? "").toLowerCase();
    const v = value.toLowerCase();
    if (op === "contains"    && !cell.includes(v))         return false;
    if (op === "equals"      && cell !== v)                return false;
    if (op === "starts_with" && !cell.startsWith(v))       return false;
    if (op === "not_equals"  && cell === v)                return false;
    if (op === "gt"          && !(parseFloat(cell) > parseFloat(v))) return false;
    if (op === "lt"          && !(parseFloat(cell) < parseFloat(v))) return false;
  }
  return true;
}

function computeTotal(rows, selectedSet, rowKey) {
  const sel = rows.filter(r => selectedSet.has(r.id));
  if (!sel.length) return null;
  const totals = {};
  for (const r of sel) {
    for (const [k, v] of Object.entries(r[rowKey] || {})) {
      const n = parseFloat(String(v || "").replace(/[$,\s]/g, ""));
      if (!isNaN(n) && isFinite(n)) totals[k] = (totals[k] || 0) + n;
    }
  }
  return Object.keys(totals).length ? totals : null;
}

function Pane({ rows, rowKey, label, color, borderColor, selected, setSelected, side, SPLIT_PAGE_SIZE, searchText, onSearchTextChange }) {
  const controlledText = typeof searchText === "string";
  const [localText, setLocalText] = useState("");
  const text = controlledText ? searchText : localText;
  const setText = (t) => {
    if (controlledText) onSearchTextChange?.(t);
    else setLocalText(t);
  };
  const [filter, setFilter] = useState({ field: "", op: "contains", value: "" });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [visCols, setVisCols] = useState(null);
  const [page, setPage] = useState(0);

  const allCols = useMemo(() => {
    for (const r of rows) {
      const keys = Object.keys(r[rowKey] || {}).filter(k => !k.startsWith("_"));
      if (keys.length) return keys;
    }
    return [];
  }, [rows, rowKey]);

  const activeCols = visCols ? [...visCols] : allCols.slice(0, 5);

  // Text search runs client-side too when data is local (applyFilter handles it).
  // When controlled (server-side), parent has pre-filtered rows; passing text
  // here is harmless (same substring match).
  const filtered = useMemo(
    () => rows.filter(r => applyFilter(r[rowKey], text, filter.field, filter.op, filter.value)),
    [rows, text, filter, rowKey]
  );

  const pages = Math.max(1, Math.ceil(filtered.length / SPLIT_PAGE_SIZE));
  const safeP = Math.min(page, pages - 1);
  const paged = filtered.slice(safeP * SPLIT_PAGE_SIZE, (safeP + 1) * SPLIT_PAGE_SIZE);
  const runningTotal = useMemo(() => computeTotal(rows, selected, rowKey), [rows, selected, rowKey]);

  const toggleSel = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const bgChecked = side === "a" ? "#EAF3EA" : "#EAF2FE";

  return (
    <div style={{ background: "white", borderRadius: "0.5rem", border: `1px solid ${borderColor}`, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ padding: "0.6rem 0.875rem", borderBottom: "1px solid var(--border-default)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <strong style={{ color, fontSize: "0.83rem" }}>
          {label} — {filtered.length}
          {selected.size > 0 && <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> · {selected.size} sel</span>}
        </strong>
        <div style={{ display: "flex", gap: "0.3rem", alignItems: "center" }}>
          <button onClick={() => setSelected(new Set(filtered.map(r => r.id)))}
            style={{ fontSize: "0.68rem", color, background: "none", border: "none", cursor: "pointer" }}>all</button>
          <button onClick={() => setSelected(new Set())}
            style={{ fontSize: "0.68rem", color: "var(--text-muted)", background: "none", border: "none", cursor: "pointer" }}>clear</button>
          <button
            data-testid={`col-picker-${side}`}
            onClick={() => setPickerOpen(p => !p)}
            title="Choose visible columns"
            style={{ background: pickerOpen ? `${color}15` : "none", border: `1px solid ${pickerOpen ? color : "var(--border-default)"}`, borderRadius: "0.25rem", padding: "0.1rem 0.35rem", cursor: "pointer", color: pickerOpen ? color : "var(--text-muted)", fontSize: "0.68rem", display: "flex", alignItems: "center", gap: "0.15rem" }}>
            <Settings size={10} /> Cols
          </button>
        </div>
      </div>

      {/* Column picker */}
      {pickerOpen && (
        <div style={{ padding: "0.45rem 0.7rem", borderBottom: "1px solid var(--border-default)", background: "var(--bg-subtle)", display: "flex", flexWrap: "wrap", gap: "0.2rem", flexShrink: 0 }}>
          {allCols.map(c => {
            const checked = visCols ? visCols.has(c) : allCols.indexOf(c) < 5;
            return (
              <label key={c} style={{ display: "inline-flex", alignItems: "center", gap: "0.2rem", fontSize: "0.68rem", cursor: "pointer", padding: "0.1rem 0.35rem", borderRadius: "0.25rem", background: checked ? `${color}12` : "white", border: `1px solid ${checked ? color + "40" : "var(--border-default)"}` }}>
                <input type="checkbox" checked={checked} onChange={() => {
                  const base = visCols || new Set(allCols.slice(0, 5));
                  const n = new Set(base);
                  n.has(c) ? n.delete(c) : n.add(c);
                  if (n.size > 0) setVisCols(n);
                }} />
                {c}
              </label>
            );
          })}
          {visCols && (
            <button onClick={() => setVisCols(null)} style={{ fontSize: "0.65rem", color: "var(--text-muted)", background: "none", border: "1px solid var(--border-default)", borderRadius: "0.25rem", padding: "0.1rem 0.3rem", cursor: "pointer" }}>
              reset
            </button>
          )}
        </div>
      )}

      {/* Running total */}
      {selected.size > 0 && runningTotal && (
        <div style={{ padding: "0.3rem 0.7rem", borderBottom: "1px solid var(--border-default)", background: "#FFFBEB", fontSize: "0.68rem", display: "flex", flexWrap: "wrap", gap: "0.4rem", alignItems: "center", flexShrink: 0 }}>
          <span style={{ fontWeight: 700, color: "#92400E" }}>Σ {selected.size} rows:</span>
          {Object.entries(runningTotal).map(([k, v]) => (
            <span key={k} style={{ color: IS_AMOUNT.test(k) ? "#B45309" : "var(--text-secondary)", fontWeight: IS_AMOUNT.test(k) ? 700 : 400, background: IS_AMOUNT.test(k) ? "#FEF9C3" : "none", padding: IS_AMOUNT.test(k) ? "0 0.3rem" : "0", borderRadius: "0.2rem" }}>
              {k}: {v.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </span>
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div style={{ padding: "0.35rem 0.625rem", borderBottom: "1px solid var(--border-default)", display: "flex", alignItems: "center", gap: "0.3rem", flexWrap: "wrap", flexShrink: 0 }}>
        <Search size={11} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
        <input
          data-testid={`search-pane-${side}`}
          value={text}
          onChange={e => { setText(e.target.value); setPage(0); }}
          placeholder="Search all fields…"
          style={{ flex: "1 1 60px", minWidth: "40px", border: "none", outline: "none", fontSize: "0.75rem", background: "transparent" }}
        />
        {allCols.length > 0 && (
          <>
            <span style={{ width: "1px", height: "12px", background: "var(--border-default)", flexShrink: 0 }} />
            <select value={filter.field}
              onChange={e => { setFilter(f => ({ ...f, field: e.target.value, value: "" })); setPage(0); }}
              style={{ fontSize: "0.7rem", border: "none", outline: "none", background: "transparent", color: filter.field ? "var(--text-primary)" : "var(--text-muted)", maxWidth: "90px" }}>
              <option value="">Field…</option>
              {allCols.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {filter.field && (
              <>
                <select value={filter.op} onChange={e => setFilter(f => ({ ...f, op: e.target.value }))}
                  style={{ fontSize: "0.7rem", border: "none", outline: "none", background: "transparent", maxWidth: "70px" }}>
                  {FILTER_OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <input value={filter.value}
                  onChange={e => { setFilter(f => ({ ...f, value: e.target.value })); setPage(0); }}
                  placeholder="value…"
                  style={{ flex: "1 1 50px", minWidth: "40px", border: "none", outline: "none", fontSize: "0.75rem", background: "transparent" }}
                />
              </>
            )}
            {(filter.field || filter.value || text) && (
              <button onClick={() => { setFilter({ field: "", op: "contains", value: "" }); setText(""); setPage(0); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "flex", padding: 0, flexShrink: 0 }}>
                <X size={11} />
              </button>
            )}
          </>
        )}
      </div>

      {/* Rows */}
      <div style={{ flex: 1, overflowY: "auto", maxHeight: "52vh" }}>
        {filtered.length === 0 ? (
          <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.8rem" }}>
            {text || filter.value ? "No rows match filter" : `No unmatched rows`}
          </div>
        ) : paged.map(r => {
          const id = r.id;
          const row = r[rowKey] || {};
          const transformedKey = rowKey + "_transformed";
          const transformed = r[transformedKey] || {};
          const checked = selected.has(id);
          return (
            <div key={id}
              data-testid={`split-pane-row-${side}-${id}`}
              onClick={() => toggleSel(id)}
              style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", padding: "0.4rem 0.625rem", borderBottom: "1px solid var(--border-default)", cursor: "pointer", fontSize: "0.78rem", background: checked ? bgChecked : "white", transition: "background 0.1s" }}>
              <input type="checkbox" checked={checked} readOnly style={{ accentColor: color, marginTop: "0.15rem", flexShrink: 0 }} />
              <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: "0.3rem 0.6rem", fontFamily: "JetBrains Mono, monospace", fontSize: "0.72rem" }}>
                {activeCols.map(k => {
                  const orig = row[k];
                  const tfmd = transformed[k];
                  const isChanged = tfmd !== undefined && tfmd !== orig;
                  const fmt = (v) => (v && typeof v === "object") ? JSON.stringify(v) : String(v ?? "");
                  return (
                    <span key={k} style={{ whiteSpace: "nowrap" }}>
                      <span style={{ color: "var(--text-muted)" }}>{k}:</span>{" "}
                      <span style={{ fontWeight: IS_AMOUNT.test(k) ? 600 : 400 }}>
                        {fmt(tfmd ?? orig)}
                      </span>
                      {isChanged && (
                        <span style={{ color: "var(--text-muted)", fontSize: "0.62rem", textDecoration: "line-through", marginLeft: "0.2rem" }}>
                          {fmt(orig)}
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {filtered.length > SPLIT_PAGE_SIZE && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.35rem 0.75rem", borderTop: "1px solid var(--border-default)", fontSize: "0.72rem", color: "var(--text-muted)", flexShrink: 0 }}>
          <span>{safeP * SPLIT_PAGE_SIZE + 1}–{Math.min((safeP + 1) * SPLIT_PAGE_SIZE, filtered.length)} of {filtered.length}</span>
          <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={safeP === 0}
              style={{ background: "none", border: "none", cursor: "pointer", opacity: safeP === 0 ? 0.3 : 1 }}>
              <ChevronLeft size={12} />
            </button>
            <span>p{safeP + 1}/{pages}</span>
            <button onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={safeP >= pages - 1}
              style={{ background: "none", border: "none", cursor: "pointer", opacity: safeP >= pages - 1 ? 0.3 : 1 }}>
              <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MatchSplitPane({
  rowsA, rowsB,
  labelA = "File A", labelB = "File B",
  colorA = "var(--sage)", colorB = "#1E40AF",
  borderColorA = "var(--sage-border)", borderColorB = "#BFDBFE",
  selectedA, setSelectedA,
  selectedB, setSelectedB,
  SPLIT_PAGE_SIZE = 25,
  searchTextA, onSearchTextChangeA,
  searchTextB, onSearchTextChangeB,
}) {
  return (
    <div data-testid="match-mode-pane" style={{ padding: "0.75rem 1rem 6rem", background: "#FAFAF7" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
        <Pane rows={rowsA} rowKey="row_a" label={labelA} color={colorA} borderColor={borderColorA}
          selected={selectedA} setSelected={setSelectedA} side="a" SPLIT_PAGE_SIZE={SPLIT_PAGE_SIZE}
          searchText={searchTextA} onSearchTextChange={onSearchTextChangeA} />
        <Pane rows={rowsB} rowKey="row_b" label={labelB} color={colorB} borderColor={borderColorB}
          selected={selectedB} setSelected={setSelectedB} side="b" SPLIT_PAGE_SIZE={SPLIT_PAGE_SIZE}
          searchText={searchTextB} onSearchTextChange={onSearchTextChangeB} />
      </div>
    </div>
  );
}
